import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Shield, Award, Clock, CheckCircle, Star, MapPin, ArrowRight, Search, Crosshair, Lock } from "lucide-react";
import { AnimalTracksHog, AnimalTracksRaccoon } from "@/components/AnimalTracks";
import { services } from "@/data/services";

const HERO_IMG = "https://images.unsplash.com/photo-1773324716220-a7821f2fc061?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxkYXJrJTIwZm9yZXN0JTIwd29vZHN8ZW58MHx8fHwxNzc0MDM3MjQ1fDA&ixlib=rb-4.1.0&q=85";

const HeroSection = () => (
  <section className="relative min-h-[90vh] flex items-center overflow-hidden noise-overlay" data-testid="hero-section">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMG})` }}
    />
    <div className="hero-overlay absolute inset-0" />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <div className="max-w-3xl">
        <p className="text-[#D95D39] uppercase text-xs sm:text-sm font-bold tracking-[0.2em] mb-4">
          Professional Wildlife Removal — St. Augustine & Surrounding Areas
        </p>
        <h1 className="text-[#F8FAFC] font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6" data-testid="hero-headline">
          They Don't Pay Rent.<br />
          We'll Handle the Eviction.
        </h1>
        <p className="text-[#9CA3AF] text-base sm:text-lg leading-relaxed max-w-2xl mb-10">
          Whether it's a raccoon throwing a party in your attic or a wild hog tearing up your yard like it owns the place — Double D Trapping LLC has been removing unwanted tenants from Florida homes and properties for over 50 years. Licensed, insured, and available 24/7 because wildlife doesn't care that it's 2am.
        </p>
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <a href="tel:9042376253" data-testid="hero-call-btn">
            <button className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-black uppercase tracking-widest text-sm sm:text-base px-8 py-4 rounded-sm btn-glow flex items-center gap-3 transition-colors duration-150">
              <Phone className="w-5 h-5" />
              Call Now — 904-237-6253
            </button>
          </a>
          <Link to="/contact" data-testid="hero-quote-btn">
            <button className="border border-[#F8FAFC]/20 hover:border-[#D95D39] text-[#F8FAFC] font-bold uppercase tracking-widest text-sm sm:text-base px-8 py-4 rounded-sm flex items-center gap-3 transition-colors duration-150">
              Get a Free Quote
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
        <p className="text-[#64748B] text-xs mt-3 tracking-wide">"We Pick Up. Day or Night."</p>
      </div>
    </div>
  </section>
);

const badges = [
  { icon: Award, text: "50 Years Experience" },
  { icon: Shield, text: "Licensed & Insured" },
  { icon: CheckCircle, text: "Federally Licensed Hog Specialist" },
  { icon: Star, text: "100% Guaranteed" },
  { icon: Clock, text: "24/7 Emergency Response" },
];

const TrustBadgesBar = () => (
  <section className="bg-[#0F1A15] border-y border-[#1F2937] py-5" data-testid="trust-badges">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
        {badges.map((b, i) => (
          <div key={i} className="flex items-center gap-2 text-[#9CA3AF]" data-testid={`badge-${i}`}>
            <b.icon className="w-4 h-4 text-[#D95D39]" />
            <span className="text-xs sm:text-sm font-semibold tracking-wide whitespace-nowrap">{b.text}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyChooseUs = () => (
  <section className="bg-[#0B110E] py-20 sm:py-28" data-testid="why-choose-us">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">Why Choose Us</p>
      <blockquote className="text-[#F8FAFC] font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug max-w-3xl mb-3">
        "We're not a call center. You call, D. Davis answers. That's been the deal for 50 years."
      </blockquote>
      <div className="w-16 h-[2px] bg-[#D95D39] mt-6 mb-12" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2937]">
        {[
          { title: "50 Years On the Job", desc: "Half a century of hands-on Florida wildlife experience. We've seen every situation and solved every problem." },
          { title: "One Phone Call", desc: "You call 904-237-6253, D. Davis picks up. Not a dispatcher, not a receptionist. The guy who does the work." },
          { title: "Licensed to Handle It All", desc: "Full state licensing plus Federal Swine Dealers License #FL 04513-16. Certified Swine Facility operator." },
          { title: "Complete Service", desc: "We don't just trap. We seal, clean, decontaminate, and replace insulation. Full-service, start to finish." },
          { title: "Guaranteed Results", desc: "Our seal-offs come with a guarantee. If something gets back in through a point we sealed — we come back." },
          { title: "24/7 Emergency", desc: "Wildlife doesn't punch a clock and neither do we. We're available around the clock, every day of the year." },
        ].map((item, i) => (
          <div key={i} className="bg-[#0B110E] p-8 sm:p-10">
            <h3 className="text-[#F8FAFC] font-bold text-base sm:text-lg mb-3">{item.title}</h3>
            <p className="text-[#9CA3AF] text-sm leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ServicesSummary = () => (
  <section className="bg-[#0F1A15] py-20 sm:py-28" data-testid="services-summary">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">Our Services</p>
      <h2 className="text-[#F8FAFC] font-black text-3xl sm:text-4xl tracking-tighter mb-12">
        What We Remove
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.filter(s => s.slug !== 'attic-sanitation').map((s) => (
          <Link
            key={s.slug}
            to={`/services/${s.slug}`}
            className="service-card group border border-[#1F2937] rounded-sm overflow-hidden bg-[#0B110E]"
            data-testid={`service-card-${s.slug}`}
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-300"
                loading="lazy"
              />
            </div>
            <div className="p-5 sm:p-6">
              <h3 className="text-[#F8FAFC] font-bold text-base sm:text-lg mb-1">{s.title}</h3>
              <p className="text-[#D95D39] text-xs font-semibold tracking-wide uppercase mb-3">{s.tagline}</p>
              <p className="text-[#9CA3AF] text-sm leading-relaxed line-clamp-2">{s.shortDesc}</p>
              <span className="inline-flex items-center gap-1.5 text-[#D95D39] text-xs font-bold uppercase tracking-wider mt-4 group-hover:gap-2.5 transition-[gap] duration-150">
                Learn More <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        ))}
      </div>
      <div className="text-center mt-10">
        <Link to="/services" data-testid="view-all-services">
          <button className="border border-[#1F2937] hover:border-[#D95D39] text-[#F8FAFC] font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm transition-colors duration-150">
            View All Services
          </button>
        </Link>
      </div>
    </div>
  </section>
);

const steps = [
  { num: "01", icon: Search, title: "Inspect", desc: "We do a full sweep of your property — attic, crawl space, roof, yard. Every gap, every entry point, every sign of activity." },
  { num: "02", icon: Crosshair, title: "Remove", desc: "Professional trapping and removal using proven methods. Humane, effective, and thorough. We don't cut corners." },
  { num: "03", icon: Lock, title: "Prevent", desc: "We seal every entry point, clean and decontaminate affected areas, and guarantee our work. They're not coming back." },
];

const ThreeStepProcess = () => (
  <section className="bg-[#0B110E] py-20 sm:py-28" data-testid="three-step-process">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">How We Work</p>
      <h2 className="text-[#F8FAFC] font-black text-3xl sm:text-4xl tracking-tighter mb-12">
        Three Steps. Problem Solved.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1F2937] border border-[#1F2937]">
        {steps.map((s) => (
          <div key={s.num} className="bg-[#0B110E] p-8 sm:p-10 relative">
            <span className="text-[#1E293B] font-black text-6xl sm:text-7xl absolute top-4 right-6 select-none">{s.num}</span>
            <s.icon className="w-8 h-8 text-[#D95D39] mb-5" />
            <h3 className="text-[#F8FAFC] font-black text-xl sm:text-2xl tracking-tight mb-3">{s.title}</h3>
            <p className="text-[#9CA3AF] text-sm leading-relaxed relative z-10">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="bg-[#0F1A15] py-20 sm:py-28" data-testid="testimonials">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">Testimonials</p>
      <h2 className="text-[#F8FAFC] font-black text-3xl sm:text-4xl tracking-tighter mb-12">
        What Our Customers Say
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { name: "Sarah M.", location: "St. Augustine", text: "Had raccoons in our attic for weeks. D. Davis came out same day, trapped them, sealed everything up. Haven't had a problem since. Worth every penny." },
          { name: "James R.", location: "Palm Coast", text: "Wild hogs destroyed our backyard. Called Double D and they handled it fast. Professional, knew exactly what they were doing. Highly recommend." },
          { name: "Linda K.", location: "St. Johns County", text: "Heard scratching in the walls at 2am. Called and D. Davis actually answered. Had someone out the next morning. That's the kind of service you can't find anymore." },
        ].map((t, i) => (
          <div key={i} className="border border-[#1F2937] p-6 sm:p-8 rounded-sm bg-[#0B110E]">
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, j) => (
                <Star key={j} className="w-4 h-4 fill-[#D95D39] text-[#D95D39]" />
              ))}
            </div>
            <p className="text-[#9CA3AF] text-sm leading-relaxed mb-6">"{t.text}"</p>
            <div>
              <p className="text-[#F8FAFC] font-bold text-sm">{t.name}</p>
              <p className="text-[#64748B] text-xs">{t.location}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ServiceAreaSection = () => (
  <section className="bg-[#0B110E] py-20 sm:py-28" data-testid="service-area">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="border border-[#1F2937] rounded-sm p-8 sm:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-8">
        <MapPin className="w-12 h-12 text-[#D95D39] shrink-0" />
        <div>
          <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-2">Service Area</p>
          <h3 className="text-[#F8FAFC] font-black text-xl sm:text-2xl tracking-tight mb-3">
            Proudly Serving Northeast Florida
          </h3>
          <p className="text-[#9CA3AF] text-sm leading-relaxed">
            We serve <span className="text-[#F8FAFC] font-semibold">St. Augustine</span>, <span className="text-[#F8FAFC] font-semibold">St. Johns County</span>, <span className="text-[#F8FAFC] font-semibold">Flagler County</span>, and surrounding areas. Not sure if you're in range? Call or text <a href="tel:9042376253" className="text-[#D95D39] font-bold hover:underline">904-237-6253</a> and we'll tell you straight.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const HogTrackDivider = () => (
  <section className="relative h-56 sm:h-72 lg:h-80 bg-[#0B110E] overflow-hidden" data-testid="hog-track-divider" aria-hidden="true">
    <AnimalTracksHog />
  </section>
);

const RaccoonTrackDivider = () => (
  <section className="relative h-56 sm:h-72 lg:h-80 bg-[#0F1A15] overflow-hidden" data-testid="raccoon-track-divider" aria-hidden="true">
    <AnimalTracksRaccoon />
  </section>
);

export default function HomePage() {
  useEffect(() => {
    document.title = "Double D Trapping LLC | Wildlife Removal | St. Augustine, FL";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Professional wildlife removal in St. Augustine, St. Johns County, and Flagler County FL. 50 years experience. Licensed, insured, 24/7 emergency. Call 904-237-6253.");
  }, []);

  return (
    <>
      <HeroSection />
      <TrustBadgesBar />
      <WhyChooseUs />
      <HogTrackDivider />
      <ServicesSummary />
      <ThreeStepProcess />
      <RaccoonTrackDivider />
      <TestimonialsSection />
      <ServiceAreaSection />
    </>
  );
}
